import React, { useState, useEffect, useMemo } from 'react';
import { Autocomplete } from 'components';
import EmployeesServices from 'services/EmployeesServices';

const Officers = ({
  name,
  label,
  value,
  onChange,
  required = false,
  validators = [],
  errorMessages = [],
  organizationId,
  disabled = false,
  type,
  excludeIds,
}) => {
  const [officers, setOfficers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const excludeSet = useMemo(
    () => new Set((excludeIds || []).filter(Boolean)),
    [excludeIds]
  );

  useEffect(() => {
    if (!organizationId) return;

    let isMounted = true;
    setLoading(true);
    setError(null);

    const fetchOfficers = async () => {
      try {
        const params = {
          organization_id: organizationId,
          status: 'Active',
          ...(type && { type }),
        };

        const response = await EmployeesServices.get(params);
        const officerData =
          response?.data?.view?.data || response?.data?.data || [];

        if (!isMounted) return;

        const formatted = Array.isArray(officerData)
          ? officerData
              .map(o => ({
                id: o.id,
                label: o.name || 'Unknown Officer',
                value: o.id,
              }))
              .filter(o => !excludeSet.has(o.id))
          : [];

        setOfficers(formatted);
      } catch (err) {
        if (isMounted) {
          setError('Failed to load officers');
          setOfficers([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchOfficers();

    return () => {
      isMounted = false;
    };
  }, [organizationId, type, excludeSet]);

  const selectedOfficer =
    officers.find(o => o.value === value) || null;

  return (
    <Autocomplete
      name={name}
      label={label}
      value={selectedOfficer}
      options={officers}
      loading={loading}
      disabled={disabled || !organizationId}
      getOptionLabel={option => option.label || ''}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      onChange={(e, newValue) =>
        onChange?.(e, newValue || null)
      }
      required={required}
      validators={validators}
      errorMessages={errorMessages}
      noOptionsText={
        !organizationId
          ? 'No organization selected'
          : error || 'No officers found'
      }
    />
  );
};

export default Officers;
